import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { consumeArrival } from '@/lib/arrival-context';

// =====================================================================
// ClaimedBanner (WP1 Task 12) — shown once after a demo claim succeeds.
// apps/dashboard/src/routes/claim-finish.ts redirects a successful claim to
// `/app/sites/:id?claimed=1`, which route-map's legacyRedirect rewrites to
// `/app/b/:id/overview?claimed=1`. overview.tsx (Task 12) was the original
// mount; setup.tsx (WP3 Task 11) mounts it too, for the same param arriving
// mid-wizard — every claim ERROR code lands on `/app?error=...` instead,
// already handled by app-index.tsx as of Task 5, so this component never
// sees those regardless of which screen mounts it.
//
// `visible` is captured ONCE via a lazy useState initializer, not read live
// from `searchParams` on every render — otherwise the banner would vanish
// the instant its own effect strips `?claimed=1` from the URL. Same
// run-once shape used elsewhere in this app for one-shot arrival signals
// (arrival-context.ts's readArrival).
//
// SINGLE STRIP OWNER (WP2 Task 8 controller ruling, 2026-08-16): this used
// to strip `?claimed` itself via `setSearchParams`. `consumeArrival()`
// (lib/arrival-context.ts) is now the ONLY code allowed to strip
// `?claimed`/`?draft` from the URL — this component still detects
// `?claimed=1` via `useSearchParams` (a plain read, no side effect), but the
// actual strip is delegated to consumeArrival so there is exactly one
// stripping mechanism instead of two independent ones that could race or
// diverge. consumeArrival strips BOTH params together (not just `claimed`)
// and clears its sessionStorage stash — a deliberate, small behavior change
// from the old claimed-only strip.
//
// CONSOLIDATION v2 A3 (binding): the original brief's `claim.acknowledged`
// emitEvent call is VOID — this component emits nothing.
// =====================================================================

export function ClaimedBanner() {
  const [searchParams] = useSearchParams();
  const [visible] = useState(() => searchParams.get('claimed') === '1');
  const strippedRef = useRef(false);

  useEffect(() => {
    if (!visible || strippedRef.current) return;
    strippedRef.current = true;
    consumeArrival();
    // Run-once on mount — `visible` never changes after the first render,
    // and re-running this on every searchParams change would fight anything
    // else on the page reading/writing the query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;

  return (
    <div role="status" className="rounded-lg border border-success-fg/30 bg-success-bg px-4 py-3 text-sm text-success-fg">
      Welcome — the demo is yours now.
    </div>
  );
}
